/**
 * GRANULLO PLATFORM EVOLUTION — Verified Blueprint Context
 * 
 * Phase 7: Verified Blueprint Structures
 * 
 * ARCHITECTURAL INVARIANT:
 * Verified Blueprints are canonical, versionable structures.
 * They support inheritance and lineage tracking.
 * They do NOT replace the Program Object.
 * 
 * Each Verified Blueprint contains:
 * - blueprint_id
 * - blueprint_version
 * - blueprint_type
 * - blueprint_graph
 * - authority_matrix
 * - baseline_signals
 * - monitoring_rules
 * - decision_defaults
 * - simulation_defaults
 * - economic_defaults
 * - lineage_ref
 * - verification_status
 * 
 * Compatible with:
 * - Scenario Library
 * - Intervention Comparison
 * - System Memory
 * - Institutional Benchmarking
 * - Future Blueprint Marketplace
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// BLUEPRINT TYPES
// ============================================================================

export const BLUEPRINT_TYPES = {
  CBDC_DISTRIBUTION: 'cbdc_distribution',
  DONATION_PROGRAM: 'donation_program',
  EMPLOYEE_BENEFITS: 'employee_benefits',
  SUBSIDY_DISTRIBUTION: 'subsidy_distribution',
  VENDOR_PAYMENT: 'vendor_payment',
  CSR_INITIATIVE: 'csr_initiative',
  CLIMATE_INCENTIVE: 'climate_incentive',
  CIRCULARITY_PROGRAM: 'circularity_program',
  CUSTOM: 'custom'
};

export const VERIFICATION_STATUS = {
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending_review',
  VERIFIED: 'verified',
  DEPRECATED: 'deprecated',
  ARCHIVED: 'archived'
};

// ============================================================================
// VERIFIED BLUEPRINT SCHEMA
// ============================================================================

/**
 * Create a new Verified Blueprint
 */
export function createVerifiedBlueprint({
  blueprint_type,
  blueprint_graph = null,
  authority_matrix = null,
  baseline_signals = [],
  monitoring_rules = [],
  decision_defaults = {},
  simulation_defaults = {},
  economic_defaults = {},
  parent_blueprint_id = null,
  metadata = {}
}) {
  const blueprint_id = `bp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const now = new Date().toISOString();
  
  return {
    // Identity
    blueprint_id,
    blueprint_version: '1.0.0',
    blueprint_type: blueprint_type || BLUEPRINT_TYPES.CUSTOM,
    
    // Structure
    blueprint_graph: blueprint_graph || { nodes: [], edges: [] },
    authority_matrix: authority_matrix || { roles: [], permissions: [], escalation_paths: [] },
    baseline_signals: baseline_signals,
    
    // Rules & Defaults
    monitoring_rules: monitoring_rules,
    decision_defaults: {
      response_windows: {
        warning: 72,      // hours
        critical: 24,
        emergency: 4,
        catastrophic: 1
      },
      escalation_enabled: true,
      auto_acknowledge: false,
      ...decision_defaults
    },
    simulation_defaults: {
      stress_scenarios: [],
      intervention_templates: [],
      ...simulation_defaults
    },
    economic_defaults: {
      economic_version: 'v1.0',
      envelope_cap_percent: 10,
      monitoring_cap_percent: 15,
      ...economic_defaults
    },
    
    // Lineage
    lineage_ref: {
      parent_blueprint_id,
      version_history: [],
      fork_count: 0,
      derived_programs: []
    },
    
    // Verification
    verification_status: VERIFICATION_STATUS.DRAFT,
    verified_at: null,
    verified_by: null,
    
    // Metadata
    created_at: now,
    updated_at: now,
    metadata: {
      name: metadata.name || 'Untitled Blueprint',
      description: metadata.description || '',
      tags: metadata.tags || [],
      category: metadata.category || 'general',
      ...metadata
    }
  };
}

/**
 * Create a new version of a blueprint
 */
export function createBlueprintVersion(blueprint, changes = {}) {
  const [major, minor, patch] = blueprint.blueprint_version.split('.').map(Number);
  const newVersion = changes.breaking 
    ? `${major + 1}.0.0` 
    : changes.feature 
      ? `${major}.${minor + 1}.0`
      : `${major}.${minor}.${patch + 1}`;
  
  const now = new Date().toISOString();
  
  return {
    ...blueprint,
    ...changes,
    blueprint_version: newVersion,
    updated_at: now,
    lineage_ref: {
      ...blueprint.lineage_ref,
      version_history: [
        ...blueprint.lineage_ref.version_history,
        {
          version: blueprint.blueprint_version,
          timestamp: now,
          changes: changes.changeLog || 'Version update'
        }
      ]
    },
    verification_status: VERIFICATION_STATUS.PENDING_REVIEW
  };
}

/**
 * Fork a blueprint (create derived blueprint)
 */
export function forkBlueprint(blueprint, forkedBy = null) {
  const forked = createVerifiedBlueprint({
    blueprint_type: blueprint.blueprint_type,
    blueprint_graph: JSON.parse(JSON.stringify(blueprint.blueprint_graph)),
    authority_matrix: JSON.parse(JSON.stringify(blueprint.authority_matrix)),
    baseline_signals: JSON.parse(JSON.stringify(blueprint.baseline_signals)),
    monitoring_rules: JSON.parse(JSON.stringify(blueprint.monitoring_rules)),
    decision_defaults: JSON.parse(JSON.stringify(blueprint.decision_defaults)),
    simulation_defaults: JSON.parse(JSON.stringify(blueprint.simulation_defaults)),
    economic_defaults: JSON.parse(JSON.stringify(blueprint.economic_defaults)),
    parent_blueprint_id: blueprint.blueprint_id,
    metadata: {
      ...blueprint.metadata,
      name: `${blueprint.metadata.name} (Fork)`,
      forked_from: blueprint.blueprint_id,
      forked_by: forkedBy,
      forked_at: new Date().toISOString()
    }
  });
  
  return forked;
}

// ============================================================================
// TEMPLATE BLUEPRINTS (Canonical Starting Points)
// ============================================================================

export const TEMPLATE_BLUEPRINTS = {
  [BLUEPRINT_TYPES.CBDC_DISTRIBUTION]: createVerifiedBlueprint({
    blueprint_type: BLUEPRINT_TYPES.CBDC_DISTRIBUTION,
    blueprint_graph: {
      nodes: [
        { id: 'central_bank', type: 'authority', layer: 0, label: 'Central Bank' },
        { id: 'commercial_bank', type: 'authority', layer: 1, label: 'Commercial Bank' },
        { id: 'merchant_network', type: 'merchant', layer: 2, label: 'Merchant Network' },
        { id: 'beneficiary_pool', type: 'beneficiary', layer: 3, label: 'Beneficiary Pool' }
      ],
      edges: [
        { source: 'central_bank', target: 'commercial_bank', type: 'authority_delegation' },
        { source: 'commercial_bank', target: 'merchant_network', type: 'distribution' },
        { source: 'merchant_network', target: 'beneficiary_pool', type: 'redemption' }
      ]
    },
    baseline_signals: [
      { id: 'txn_volume', type: 'structural', name: 'Transaction Volume' },
      { id: 'redemption_rate', type: 'behaviour', name: 'Redemption Rate' },
      { id: 'merchant_concentration', type: 'structural', name: 'Merchant Concentration' }
    ],
    economic_defaults: {
      economic_version: 'v1.0',
      envelope_cap_percent: 10
    },
    metadata: {
      name: 'CBDC Distribution Blueprint',
      description: 'Standard CBDC distribution structure with central bank, commercial banks, merchants, and beneficiaries',
      category: 'cbdc',
      tags: ['cbdc', 'government', 'distribution']
    }
  }),
  
  [BLUEPRINT_TYPES.DONATION_PROGRAM]: createVerifiedBlueprint({
    blueprint_type: BLUEPRINT_TYPES.DONATION_PROGRAM,
    blueprint_graph: {
      nodes: [
        { id: 'foundation', type: 'authority', layer: 0, label: 'Foundation' },
        { id: 'program_manager', type: 'authority', layer: 1, label: 'Program Manager' },
        { id: 'partner_ngo', type: 'merchant', layer: 2, label: 'Partner NGO' },
        { id: 'beneficiaries', type: 'beneficiary', layer: 3, label: 'Beneficiaries' }
      ],
      edges: [
        { source: 'foundation', target: 'program_manager', type: 'authority_delegation' },
        { source: 'program_manager', target: 'partner_ngo', type: 'grant_distribution' },
        { source: 'partner_ngo', target: 'beneficiaries', type: 'direct_benefit' }
      ]
    },
    baseline_signals: [
      { id: 'grant_utilization', type: 'structural', name: 'Grant Utilization' },
      { id: 'beneficiary_reach', type: 'behaviour', name: 'Beneficiary Reach' },
      { id: 'impact_measurement', type: 'informational', name: 'Impact Measurement' }
    ],
    economic_defaults: {
      economic_version: 'v1.0',
      envelope_cap_percent: 6
    },
    metadata: {
      name: 'Donation Program Blueprint',
      description: 'Standard donation/grant program structure for CSR and foundation initiatives',
      category: 'donation',
      tags: ['csr', 'donation', 'nonprofit']
    }
  }),
  
  [BLUEPRINT_TYPES.EMPLOYEE_BENEFITS]: createVerifiedBlueprint({
    blueprint_type: BLUEPRINT_TYPES.EMPLOYEE_BENEFITS,
    blueprint_graph: {
      nodes: [
        { id: 'hr_department', type: 'authority', layer: 0, label: 'HR Department' },
        { id: 'benefits_admin', type: 'authority', layer: 1, label: 'Benefits Admin' },
        { id: 'vendor_network', type: 'merchant', layer: 2, label: 'Vendor Network' },
        { id: 'employees', type: 'beneficiary', layer: 3, label: 'Employees' }
      ],
      edges: [
        { source: 'hr_department', target: 'benefits_admin', type: 'policy_delegation' },
        { source: 'benefits_admin', target: 'vendor_network', type: 'vendor_agreement' },
        { source: 'vendor_network', target: 'employees', type: 'benefit_redemption' }
      ]
    },
    baseline_signals: [
      { id: 'utilization_rate', type: 'behaviour', name: 'Benefit Utilization Rate' },
      { id: 'vendor_diversity', type: 'structural', name: 'Vendor Diversity' },
      { id: 'employee_satisfaction', type: 'informational', name: 'Employee Satisfaction' }
    ],
    economic_defaults: {
      economic_version: 'v1.0',
      envelope_cap_percent: 10
    },
    metadata: {
      name: 'Employee Benefits Blueprint',
      description: 'Standard employee benefits program structure',
      category: 'enterprise',
      tags: ['hr', 'benefits', 'enterprise']
    }
  })
};

// ============================================================================
// BENCHMARKING PRIMITIVES
// ============================================================================

/**
 * Compare two blueprints for similarity
 * Returns similarity score 0-100
 */
export function compareBlueprintStructure(blueprintA, blueprintB) {
  if (!blueprintA || !blueprintB) return 0;
  
  const graphA = blueprintA.blueprint_graph;
  const graphB = blueprintB.blueprint_graph;
  
  // Compare node counts
  const nodeCountDiff = Math.abs((graphA.nodes?.length || 0) - (graphB.nodes?.length || 0));
  const nodeScore = Math.max(0, 100 - nodeCountDiff * 10);
  
  // Compare edge counts
  const edgeCountDiff = Math.abs((graphA.edges?.length || 0) - (graphB.edges?.length || 0));
  const edgeScore = Math.max(0, 100 - edgeCountDiff * 10);
  
  // Compare node type distribution
  const getTypeDistribution = (nodes) => {
    return (nodes || []).reduce((acc, n) => {
      acc[n.type] = (acc[n.type] || 0) + 1;
      return acc;
    }, {});
  };
  
  const distA = getTypeDistribution(graphA.nodes);
  const distB = getTypeDistribution(graphB.nodes);
  const allTypes = new Set([...Object.keys(distA), ...Object.keys(distB)]);
  
  let typeMatchScore = 0;
  allTypes.forEach(type => {
    const countA = distA[type] || 0;
    const countB = distB[type] || 0;
    typeMatchScore += Math.min(countA, countB) / Math.max(countA, countB, 1);
  });
  typeMatchScore = (typeMatchScore / allTypes.size) * 100;
  
  // Weighted average
  return Math.round((nodeScore * 0.3 + edgeScore * 0.3 + typeMatchScore * 0.4));
}

/**
 * Get blueprint family (all blueprints sharing lineage)
 */
export function getBlueprintFamily(blueprint, allBlueprints) {
  const family = [];
  
  // Find parent chain
  let current = blueprint;
  while (current?.lineage_ref?.parent_blueprint_id) {
    const parent = allBlueprints.find(b => b.blueprint_id === current.lineage_ref.parent_blueprint_id);
    if (parent) {
      family.unshift(parent);
      current = parent;
    } else {
      break;
    }
  }
  
  // Add self
  family.push(blueprint);
  
  // Find children
  const findChildren = (bp) => {
    const children = allBlueprints.filter(b => b.lineage_ref?.parent_blueprint_id === bp.blueprint_id);
    children.forEach(child => {
      family.push(child);
      findChildren(child);
    });
  };
  findChildren(blueprint);
  
  return family;
}

/**
 * Calculate blueprint performance score (placeholder for real metrics)
 * This will be enhanced when real program data is available
 */
export function calculateBlueprintPerformanceScore(blueprint, programMetrics = []) {
  // Base score from structure completeness
  let score = 0;
  
  // Graph completeness (25 points)
  const hasNodes = blueprint.blueprint_graph?.nodes?.length > 0;
  const hasEdges = blueprint.blueprint_graph?.edges?.length > 0;
  score += hasNodes ? 15 : 0;
  score += hasEdges ? 10 : 0;
  
  // Authority matrix (20 points)
  const hasRoles = blueprint.authority_matrix?.roles?.length > 0;
  const hasEscalation = blueprint.authority_matrix?.escalation_paths?.length > 0;
  score += hasRoles ? 10 : 0;
  score += hasEscalation ? 10 : 0;
  
  // Signals defined (20 points)
  score += Math.min(20, blueprint.baseline_signals?.length * 5);
  
  // Monitoring rules (15 points)
  score += Math.min(15, blueprint.monitoring_rules?.length * 5);
  
  // Verification status (20 points)
  if (blueprint.verification_status === VERIFICATION_STATUS.VERIFIED) {
    score += 20;
  } else if (blueprint.verification_status === VERIFICATION_STATUS.PENDING_REVIEW) {
    score += 10;
  }
  
  return Math.min(100, score);
}

// ============================================================================
// VERIFIED BLUEPRINT CONTEXT
// ============================================================================

const VerifiedBlueprintContext = createContext(null);

export function VerifiedBlueprintProvider({ children }) {
  // Blueprint registry
  const [blueprintRegistry, setBlueprintRegistry] = useState(() => {
    // Initialize with template blueprints
    const templates = {};
    Object.values(TEMPLATE_BLUEPRINTS).forEach(bp => {
      templates[bp.blueprint_id] = { ...bp, verification_status: VERIFICATION_STATUS.VERIFIED };
    });
    return templates;
  });
  
  // Program to blueprint mapping
  const [programBlueprintMap, setProgramBlueprintMap] = useState({});
  
  /**
   * Register a new blueprint
   */
  const registerBlueprint = useCallback((blueprint) => {
    setBlueprintRegistry(prev => ({
      ...prev,
      [blueprint.blueprint_id]: blueprint
    }));
    return blueprint.blueprint_id;
  }, []);
  
  /**
   * Create and register a new blueprint
   */
  const createBlueprint = useCallback((config) => {
    const blueprint = createVerifiedBlueprint(config);
    registerBlueprint(blueprint);
    return blueprint;
  }, [registerBlueprint]);
  
  /**
   * Update a blueprint
   */
  const updateBlueprint = useCallback((blueprintId, updates) => {
    setBlueprintRegistry(prev => {
      const existing = prev[blueprintId];
      if (!existing) return prev;
      
      return {
        ...prev,
        [blueprintId]: {
          ...existing,
          ...updates,
          updated_at: new Date().toISOString()
        }
      };
    });
  }, []);
  
  /**
   * Create a new version of a blueprint
   */
  const versionBlueprint = useCallback((blueprintId, changes = {}) => {
    const existing = blueprintRegistry[blueprintId];
    if (!existing) return null;
    
    const newVersion = createBlueprintVersion(existing, changes);
    registerBlueprint(newVersion);
    return newVersion;
  }, [blueprintRegistry, registerBlueprint]);
  
  /**
   * Fork a blueprint
   */
  const forkBlueprintById = useCallback((blueprintId, forkedBy = null) => {
    const existing = blueprintRegistry[blueprintId];
    if (!existing) return null;
    
    const forked = forkBlueprint(existing, forkedBy);
    registerBlueprint(forked);
    
    // Update original's fork count
    updateBlueprint(blueprintId, {
      lineage_ref: {
        ...existing.lineage_ref,
        fork_count: (existing.lineage_ref.fork_count || 0) + 1
      }
    });
    
    return forked;
  }, [blueprintRegistry, registerBlueprint, updateBlueprint]);
  
  /**
   * Verify a blueprint
   */
  const verifyBlueprint = useCallback((blueprintId, verifiedBy) => {
    updateBlueprint(blueprintId, {
      verification_status: VERIFICATION_STATUS.VERIFIED,
      verified_at: new Date().toISOString(),
      verified_by: verifiedBy
    });
  }, [updateBlueprint]);
  
  /**
   * Assign blueprint to program
   */
  const assignBlueprintToProgram = useCallback((programId, blueprintId) => {
    const blueprint = blueprintRegistry[blueprintId];
    if (!blueprint) return false;
    
    setProgramBlueprintMap(prev => ({
      ...prev,
      [programId]: blueprintId
    }));
    
    // Update blueprint's derived programs
    updateBlueprint(blueprintId, {
      lineage_ref: {
        ...blueprint.lineage_ref,
        derived_programs: [...(blueprint.lineage_ref.derived_programs || []), programId]
      }
    });
    
    return true;
  }, [blueprintRegistry, updateBlueprint]);
  
  /**
   * Get blueprint for a program
   */
  const getProgramBlueprint = useCallback((programId) => {
    const blueprintId = programBlueprintMap[programId];
    return blueprintId ? blueprintRegistry[blueprintId] : null;
  }, [programBlueprintMap, blueprintRegistry]);
  
  /**
   * Get blueprint lineage
   */
  const getBlueprintLineage = useCallback((blueprintId) => {
    const blueprint = blueprintRegistry[blueprintId];
    if (!blueprint) return null;
    
    return getBlueprintFamily(blueprint, Object.values(blueprintRegistry));
  }, [blueprintRegistry]);
  
  /**
   * Get blueprints by type
   */
  const getBlueprintsByType = useCallback((type) => {
    return Object.values(blueprintRegistry).filter(bp => bp.blueprint_type === type);
  }, [blueprintRegistry]);
  
  /**
   * Get verified blueprints only
   */
  const getVerifiedBlueprints = useCallback(() => {
    return Object.values(blueprintRegistry).filter(
      bp => bp.verification_status === VERIFICATION_STATUS.VERIFIED
    );
  }, [blueprintRegistry]);
  
  /**
   * Compare blueprints
   */
  const compareBlueprints = useCallback((blueprintIdA, blueprintIdB) => {
    const bpA = blueprintRegistry[blueprintIdA];
    const bpB = blueprintRegistry[blueprintIdB];
    return compareBlueprintStructure(bpA, bpB);
  }, [blueprintRegistry]);
  
  /**
   * Get blueprint performance
   */
  const getBlueprintPerformance = useCallback((blueprintId) => {
    const blueprint = blueprintRegistry[blueprintId];
    if (!blueprint) return null;
    return calculateBlueprintPerformanceScore(blueprint);
  }, [blueprintRegistry]);
  
  // Registry statistics
  const registryStats = useMemo(() => ({
    totalBlueprints: Object.keys(blueprintRegistry).length,
    verifiedCount: Object.values(blueprintRegistry).filter(
      bp => bp.verification_status === VERIFICATION_STATUS.VERIFIED
    ).length,
    draftCount: Object.values(blueprintRegistry).filter(
      bp => bp.verification_status === VERIFICATION_STATUS.DRAFT
    ).length,
    byType: Object.values(blueprintRegistry).reduce((acc, bp) => {
      acc[bp.blueprint_type] = (acc[bp.blueprint_type] || 0) + 1;
      return acc;
    }, {}),
    programAssignments: Object.keys(programBlueprintMap).length
  }), [blueprintRegistry, programBlueprintMap]);
  
  // Context value
  const value = useMemo(() => ({
    // Constants
    BLUEPRINT_TYPES,
    VERIFICATION_STATUS,
    TEMPLATE_BLUEPRINTS,
    
    // State
    blueprintRegistry,
    programBlueprintMap,
    registryStats,
    
    // CRUD Operations
    createBlueprint,
    registerBlueprint,
    updateBlueprint,
    versionBlueprint,
    forkBlueprintById,
    verifyBlueprint,
    
    // Program Operations
    assignBlueprintToProgram,
    getProgramBlueprint,
    
    // Query Operations
    getBlueprintLineage,
    getBlueprintsByType,
    getVerifiedBlueprints,
    
    // Benchmarking
    compareBlueprints,
    getBlueprintPerformance,
    
    // Static functions
    createVerifiedBlueprint,
    createBlueprintVersion,
    forkBlueprint,
    compareBlueprintStructure,
    getBlueprintFamily,
    calculateBlueprintPerformanceScore
  }), [
    blueprintRegistry,
    programBlueprintMap,
    registryStats,
    createBlueprint,
    registerBlueprint,
    updateBlueprint,
    versionBlueprint,
    forkBlueprintById,
    verifyBlueprint,
    assignBlueprintToProgram,
    getProgramBlueprint,
    getBlueprintLineage,
    getBlueprintsByType,
    getVerifiedBlueprints,
    compareBlueprints,
    getBlueprintPerformance
  ]);
  
  return (
    <VerifiedBlueprintContext.Provider value={value}>
      {children}
    </VerifiedBlueprintContext.Provider>
  );
}

export function useVerifiedBlueprint() {
  const context = useContext(VerifiedBlueprintContext);
  if (!context) {
    throw new Error('useVerifiedBlueprint must be used within VerifiedBlueprintProvider');
  }
  return context;
}

/**
 * Optional hook for use outside provider
 */
export function useVerifiedBlueprintOptional() {
  return useContext(VerifiedBlueprintContext);
}

export default VerifiedBlueprintContext;
